import { useState, useEffect } from "react";
import { Users, RefreshCw, TrendingUp, TrendingDown, ChevronDown, ChevronRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_STYLES = {
  BUY: { bg: "bg-emerald-500/20", border: "border-emerald-400/40", text: "text-emerald-300" },
  CALLS: { bg: "bg-cyan-500/20", border: "border-cyan-400/40", text: "text-cyan-300" },
  SELL: { bg: "bg-rose-500/15", border: "border-rose-400/30", text: "text-rose-300" },
};

export default function PoliticiansWatch({ onTickerClick, portfolioTickers = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set(["pelosi"]));

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/curated/politicians`, { timeout: 20000 });
      setData(r.data);
    } catch (e) {
      console.error("Politicians fetch error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggle = (id) => {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  const ownedSet = new Set((portfolioTickers || []).map((t) => (t || "").toUpperCase()));

  return (
    <div className="glass-card rounded-xl border border-fuchsia-500/20 overflow-hidden" data-testid="politicians-panel">
      <div className="bg-gradient-to-r from-fuchsia-600/15 via-pink-600/10 to-transparent px-5 py-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-fuchsia-500/20 border border-fuchsia-400/30">
            <Users size={18} className="text-fuchsia-300" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">Politiciens à suivre</h3>
            <p className="text-slate-400 text-xs">Trades divulgués (STOCK Act) — Pelosi, Crenshaw, Tuberville, Khanna</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-2 rounded-lg bg-slate-700/60 hover:bg-slate-600 text-slate-300 transition disabled:opacity-50"
          data-testid="politicians-refresh-btn"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading && !data && <div className="p-6 text-center text-slate-400 text-sm">Chargement…</div>}

      {data && data.politicians && (
        <div className="divide-y divide-slate-700/60">
          {data.politicians.map((pol) => {
            const isOpen = expanded.has(pol.id);
            return (
              <div key={pol.id} data-testid={`politician-${pol.id}`}>
                <button
                  onClick={() => toggle(pol.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-800/40 transition"
                >
                  <div className="flex items-center gap-3 text-left">
                    {isOpen ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-semibold text-sm">{pol.flag} {pol.name}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 font-medium">
                          {pol.win_rate}% win rate
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{pol.role}</div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 hidden sm:block max-w-xs text-right">{pol.note}</div>
                </button>

                {isOpen && pol.recent_trades && (
                  <div className="px-4 pb-4 space-y-1">
                    {pol.recent_trades.map((t, i) => {
                      const style = ACTION_STYLES[t.action] || ACTION_STYLES.BUY;
                      const owned = ownedSet.has((t.ticker || "").toUpperCase());
                      const posChange = (t.change_pct || 0) >= 0;
                      return (
                        <div
                          key={i}
                          onClick={() => onTickerClick && onTickerClick(t.ticker)}
                          className="flex items-center justify-between p-2.5 rounded-lg bg-slate-800/40 hover:bg-slate-700/50 cursor-pointer transition"
                          data-testid={`trade-${pol.id}-${t.ticker}-${i}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-20 shrink-0">
                              <div className="text-xs text-slate-500 font-mono">{t.date}</div>
                              {t.filed && <div className="text-[9px] text-slate-600 font-mono">filed {t.filed.slice(5)}</div>}
                            </div>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${style.bg} ${style.border} ${style.text}`}>
                              {t.action}
                            </span>
                            <span className="text-white font-bold text-sm">{t.ticker}</span>
                            {owned && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 font-medium">
                                HOLDING
                              </span>
                            )}
                            <span className="text-xs text-slate-400 truncate hidden md:inline">{t.size}</span>
                          </div>
                          <div className="text-right shrink-0 flex items-center gap-3">
                            <span className="text-xs text-slate-400">{t.value}</span>
                            {t.current_price && (
                              <div className="text-right">
                                <div className="text-xs text-white font-medium">${t.current_price.toFixed(2)}</div>
                                <div className={`text-[10px] flex items-center gap-0.5 justify-end ${posChange ? "text-emerald-400" : "text-rose-400"}`}>
                                  {posChange ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
                                  {posChange ? "+" : ""}{(t.change_pct || 0).toFixed(1)}%
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {data && data.note && (
        <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-800/30 border-t border-slate-700/60">
          {data.note}
        </div>
      )}
    </div>
  );
}
